import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Paper,
  List,
  ListItem,
  Button,
  CircularProgress,
  Avatar,
  useTheme,
  Select,
  MenuItem,
  SelectChangeEvent,
  Modal,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ContentType, ClassId, AspectRatio, GeneratedImage } from '../types';
import { generateImage, generateImageSession, generateImageAccess } from '../services/api';
import ReactMarkdown from 'react-markdown';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

interface ImageGenerationTabProps {
  contentType: ContentType;
  classId?: ClassId;
  sessionId?: string;
  userId?: number;
  accessId?: string;
}

const contentDescriptions: Record<ContentType, string> = {
  'Normal': '이미지 생성 AI를 사용해 다양한 이미지를 만들어보세요.',
  'AI 크리에이터': '창의적인 이미지를 생성하고 예술 작품을 만들어보세요.',
  'AI 윤리 & 사회변화': 'AI 이미지 생성이 가져올 사회적 영향을 탐구해보세요.',
  '차세대 기업가 정신': '비즈니스에 활용할 수 있는 이미지를 생성해보세요.',
  '미래 사회 탐험가': '미래 사회의 모습을 이미지로 표현해보세요.',
  '미스터리 코드': '코딩과 관련된 이미지를 생성해보세요.',
  'AI 트랜드 익스플로러': 'AI 트랜드를 탐험하는데 필요한 이미지를 만들어보세요.',
};

const ImageGenerationTab: React.FC<ImageGenerationTabProps> = ({ contentType, classId, sessionId, userId, accessId }) => {
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('16:9');
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>(() => {
    const savedImages = localStorage.getItem('generated_images');
    return savedImages ? JSON.parse(savedImages) : [];
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null);

  useEffect(() => {
    localStorage.setItem('generated_images', JSON.stringify(generatedImages));
  }, [generatedImages]);

  const handleAspectRatioChange = (event: SelectChangeEvent) => {
    setAspectRatio(event.target.value as AspectRatio);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setIsLoading(true);
    try {
      let imageUrl: string;
      
      // API 호출 우선순위: accessId > sessionId > classId
      if (accessId) {
        imageUrl = await generateImageAccess(prompt, accessId, aspectRatio);
      } else if (sessionId && userId) {
        imageUrl = await generateImageSession(prompt, sessionId, userId, aspectRatio);
      } else if (classId) {
        imageUrl = await generateImage(prompt, classId, aspectRatio);
      } else {
        throw new Error('No valid API configuration found');
      }
      
      const newImage: GeneratedImage = {
        url: imageUrl,
        prompt,
        timestamp: Date.now(),
      };
      setGeneratedImages([newImage, ...generatedImages]);
      setPrompt('');
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageClick = (image: GeneratedImage) => {
    setSelectedImage(image);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  return (
    <Box 
      sx={{ 
        height: 'calc(100vh - 180px)',
        maxHeight: 'calc(100vh - 180px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 3,
      }}
    >
      {/* 고정 헤더 */}
      <Typography 
        variant="h5" 
        sx={{
          color: 'white',
          fontWeight: 600,
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          mb: 3,
          flexShrink: 0,
        }}
      >
        {contentDescriptions[contentType]}
      </Typography>

      {/* 고정 프롬프트 입력 섹션 */}
      <Paper 
        sx={{ 
          p: 3,
          mb: 3,
          background: 'transparent',
          backdropFilter: 'none',
          border: 'none',
          borderRadius: '16px',
          boxShadow: 'none',
          flexShrink: 0,
        }}
      >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleGenerate();
                    }
                  }}
                  placeholder="이미지 생성을 위한 프롬프트를 입력하세요... (Enter: 생성)"
                  variant="outlined"
                  disabled={isLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'white',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: 'white',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.6)',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Select
                  fullWidth
                  value={aspectRatio}
                  onChange={handleAspectRatioChange}
                  disabled={isLoading}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'white',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.1)',
                          },
                          '&.Mui-selected': {
                            background: 'rgba(255, 255, 255, 0.2)',
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="1:1">1:1</MenuItem>
                  <MenuItem value="16:9">16:9</MenuItem>
                  <MenuItem value="9:16">9:16</MenuItem>
                  <MenuItem value="4:3">4:3</MenuItem>
                  <MenuItem value="3:4">3:4</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleGenerate}
                  disabled={isLoading}
                  sx={{
                    height: '56px',
                    borderRadius: '12px',
                    background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    fontWeight: 'bold',
                    '&:hover': {
                      background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.2))',
                    },
                    '&:disabled': {
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                >
                  {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      생성 중...
                    </Box>
                  ) : (
                    '생성하기'
                  )}
                </Button>
              </Grid>
            </Grid>
          </Paper>

      {/* 스크롤 가능한 이미지 갤러리 */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          mt: 2,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '10px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.4)',
            },
          },
        }}
      >
        <Grid container spacing={3}>
          {generatedImages.map((image, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={image.timestamp}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  background: 'transparent',
                  backdropFilter: 'none',
                  border: 'none',
                  boxShadow: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 'none',
                    background: 'transparent',
                  },
                }}
                onClick={() => handleImageClick(image)}
              >
                <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      fontWeight: 500,
                    }}
                  >
                    프롬프트: {image.prompt}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '100%',
                    background: 'rgba(0, 0, 0, 0.2)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <Box
                    component="img"
                    src={image.url}
                    alt={image.prompt}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      p: 1,
                      borderRadius: '8px',
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Modal
        open={!!selectedImage}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            outline: 'none',
            background: 'transparent',
            backdropFilter: 'none',
            border: 'none',
            borderRadius: '16px',
            boxShadow: 'none',
            p: 3,
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          
          {selectedImage && (
            <>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  mb: 2,
                  fontWeight: 600,
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                }}
              >
                {selectedImage.prompt}
              </Typography>
              <Box
                component="img"
                src={selectedImage.url}
                alt={selectedImage.prompt}
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxWidth: '80vw',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              />
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default ImageGenerationTab; 